<template>
  <div class="page">

    <!-- ================= HEADER ================= -->
    <div class="page-head">
      <div>
        <h1>Credit Requests</h1>
      </div>

      <div class="actions">
        <div class="search-mini">
          <i class="bx bx-search"></i>
          <input
            v-model="query"
            type="text"
            placeholder="Search name, phone, user id..."
          />
        </div>

        <select v-model="statusFilter" class="select">
          <option value="">All Status</option>
          <option value="SUBMITTED">Submitted</option>
          <option value="APPROVED">Approved</option>
          <option value="REJECTED">Rejected</option>
        </select>

        <select v-model="sortBy" class="select">
          <option value="NEWEST">Newest First</option>
          <option value="OLDEST">Oldest First</option>
          <option value="AMOUNT_DESC">Amount High → Low</option>
          <option value="AMOUNT_ASC">Amount Low → High</option>
        </select>

        <button class="btn ghost" @click="fetchRequests">
          <i class="bx bx-refresh"></i>
          Refresh
        </button>
      </div>
    </div>

    <!-- ================= KPI ================= -->
    <div class="grid cols-4">
      <div class="card stat">
        <strong>Total Requests</strong>
        <div class="metric">{{ requests.length }}</div>
      </div>

      <div class="card stat">
        <strong>Submitted</strong>
        <div class="metric">{{ submittedCount }}</div>
      </div>

      <div class="card stat">
        <strong>Approved</strong>
        <div class="metric">{{ approvedCount }}</div>
      </div>

      <div class="card stat">
        <strong>Rejected</strong>
        <div class="metric">{{ rejectedCount }}</div>
      </div>
    </div>

    <!-- ================= TABLE ================= -->
    <div class="card" style="margin-top:14px;">
      <table class="table">
        <thead>
          <tr>
            <th>USER</th>
            <th>AMOUNT</th>
            <th>NOTE</th>
            <th>STATUS</th>
            <th>REQUESTED AT</th>
            <th style="text-align:right">ACTIONS</th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="r in filtered" :key="r.creq_id">
            <td>
              <div class="usr">
                <div class="avatar">
                  {{ r.user_fname?.charAt(0) }}
                </div>
                <div>
                  <div class="usr-name">
                    {{ r.user_fname }} {{ r.user_lname }}
                  </div>
                  <div class="row-muted">
                    {{ r.user_mobile }}
                  </div>
                </div>
              </div>
            </td>

            <td>₹ {{ r.creq_amount }}</td>

            <td>{{ r.creq_remark || "-" }}</td>

            <td>
              <span class="status-pill" :class="statusClass(r.creq_status)">
                <i class="bx" :class="statusIcon(r.creq_status)"></i>
                {{ r.creq_status }}
              </span>
            </td>

            <td class="row-muted">
              {{ r.creq_datetime }}
            </td>

            <td style="text-align:right">
              <div class="row-actions">
                <button
                  v-if="r.creq_status==='submitted'"
                  class="icon-mini"
                  @click="openApprove(r)"
                >
                  <i class="bx bx-check-circle"></i>
                </button>

                <button
                  v-if="r.creq_status==='submitted'"
                  class="icon-mini danger"
                  @click="openReject(r)"
                >
                  <i class="bx bx-x-circle"></i>
                </button>
              </div>
            </td>
          </tr>

          <tr v-if="filtered.length===0">
            <td colspan="6" style="padding:20px;">
              <div class="empty">
                <i class="bx bx-file-find"></i>
                <div>No requests found</div>
              </div>
            </td>
          </tr>

        </tbody>
      </table>
    </div>

    <!-- ================= APPROVE MODAL ================= -->
    <div v-if="approve.open" class="modal-backdrop">
      <div class="modal">

        <div class="modal-head">
          <strong>Approve Credit</strong>
          <button class="icon-mini" @click="closeApprove">
            <i class="bx bx-x"></i>
          </button>
        </div>

        <div class="modal-body">
          <div class="field">
            <label>Approved Amount</label>
            <input class="input" v-model="approve.amount" />
          </div>

          <div class="field">
            <label>Tenure (months)</label>
            <input class="input" v-model="approve.tenure" />
          </div>
        </div>

        <div class="modal-foot">
          <button class="btn ghost" @click="closeApprove">Cancel</button>
          <button class="btn primary" @click="confirmApprove">
            Approve
          </button>
        </div>

      </div>
    </div>

    <!-- ================= REJECT MODAL ================= -->
    <div v-if="reject.open" class="modal-backdrop">
      <div class="modal">

        <div class="modal-head">
          <strong>Reject Credit</strong>
          <button class="icon-mini" @click="closeReject">
            <i class="bx bx-x"></i>
          </button>
        </div>

        <div class="modal-body">
          <textarea
            class="notes"
            v-model="reject.reason"
            placeholder="Reason..."
          ></textarea>
        </div>

        <div class="modal-foot">
          <button class="btn ghost" @click="closeReject">Cancel</button>
          <button class="btn primary" @click="confirmReject">
            Reject
          </button>
        </div>

      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useToast } from "vue-toastification";
import {
  getCreditRequests,
  approveCreditRequest ,
  rejectCreditRequest
} from "@/apis/creditcon";

const toast = useToast();

const requests = ref([]);

const query = ref("");
const statusFilter = ref("");
const sortBy = ref("NEWEST");
const admin_id = ref("");
const page_no = ref(0);
const total_records = ref(0);


/* ================= KPI ================= */

const submittedCount = computed(() =>
  requests.value.filter(x=>x.creq_status==="submitted").length
);
const approvedCount = computed(() =>
  requests.value.filter(x=>x.creq_status==="approved").length
);
const rejectedCount = computed(() =>
  requests.value.filter(x=>x.creq_status==="rejected").length
);

/* ================= FILTER ================= */

const filtered = computed(() => {
  let list = requests.value;

  if(statusFilter.value){
    list = list.filter(
      x=>x.creq_status.toUpperCase()===statusFilter.value
    );
  }

  if(query.value){
    const q=query.value.toLowerCase();
    list = list.filter(x =>
      String(x.user_fname).toLowerCase().includes(q) ||
      String(x.user_mobile).includes(q)
    );
  }

  return list;
});

/* ================= STATUS UI ================= */

const statusClass = (s)=>{
  if(s==="approved") return "success";
  if(s==="submitted") return "warning";
  if(s==="rejected") return "danger";
  return "";
};

const statusIcon = (s)=>{
  if(s==="approved") return "bx-check-circle";
  if(s==="submitted") return "bx-time-five";
  if(s==="rejected") return "bx-x-circle";
  return "bx-info-circle";
};

/* ================= LOAD ================= */

const fetchRequests = async () => {

  const res = await getCreditRequests({
    admin_id: admin_id.value,
    page_no: page_no.value,
    keyword: query.value,
    status: statusFilter.value,
    sort_by: sortBy.value,
  });

  if (res.status) {
    requests.value = res.records || [];
    total_records.value = parseInt(res.total || 0);
  } else {
    toast.error(res.success || "Failed to load requests");
  }
};


/* ================= APPROVE ================= */

const approve = ref({open:false,req:null,amount:"",tenure:""});

const openApprove=(r)=>{
  approve.value={open:true,req:r,amount:r.creq_amount,tenure:""};
};

const closeApprove=()=>approve.value.open=false;

const confirmApprove = async () => {

  const res = await approveCreditRequest({
    creq_id: approve.value.req.creq_id,
    admin_id: admin_id.value,
    amount: approve.value.amount,
    tenure: approve.value.tenure
  });

  if(res.status){
    toast.success("Approved");
    closeApprove();
    fetchRequests();
  }else{
    toast.error(res.success || "Approve failed");
  }

};


/* ================= REJECT ================= */

const reject = ref({open:false,req:null,reason:""});

const openReject=(r)=>{
  reject.value={open:true,req:r,reason:""};
};

const closeReject=()=>reject.value.open=false;

const confirmReject=async()=>{
  const res = await rejectCreditRequest(
    reject.value.req.creq_id,
    admin_id.value,
    reject.value.reason
  );
  if(res.status){
    toast.success("Rejected");
    closeReject();
    fetchRequests();
  }else toast.error(res.success);
};

/* ================= INIT ================= */

onMounted(async () => {
  const raw = sessionStorage.getItem("ldata");

  if (!raw) {
    toast.error("Session expired");
    return;
  }

  const ldata = JSON.parse(raw);
  admin_id.value = ldata.user_id || "";

  if (!admin_id.value) {
    toast.error("Invalid admin session");
    return;
  }

  fetchRequests();
});

</script>
<style scoped>
/* ============================
   GRID + STATS
============================ */

.grid {
  display: grid;
  gap: 14px;
}

.grid.cols-4 {
  grid-template-columns: repeat(4, minmax(0,1fr));
}

.stat {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.stat strong {
  font-size: 13px;
  font-weight: 800;
}

.metric {
  font-size: 26px;
  font-weight: 900;
}

/* ============================
   TABLE
============================ */

.table {
  width: 100%;
  border-collapse: collapse;
}

.table th {
  text-align: left;
  font-size: 12px;
  font-weight: 800;
  color: #475569;
  padding: 12px;
  border-bottom: 1px solid var(--line);
}

.table td {
  padding: 12px;
  font-size: 13px;
  font-weight: 600;
  border-bottom: 1px solid var(--line);
}

.table tr:hover {
  background: rgba(241,245,249,0.6);
}

/* ============================
   USER CELL
============================ */

.usr {
  display: flex;
  align-items: center;
  gap: 10px;
}

.avatar {
  height: 36px;
  width: 36px;
  border-radius: 50%;
  background: rgba(46,109,231,0.12);
  color: var(--primary);
  display: grid;
  place-items: center;
  font-weight: 900;
}

.usr-name {
  font-weight: 800;
}

.row-muted {
  font-size: 12px;
  color: #64748b;
}

/* ============================
   STATUS PILL
============================ */

.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
  border: 1px solid var(--line);
}

.status-pill.success {
  background: rgba(34,197,94,0.12);
  color: #15803d;
  border-color: rgba(34,197,94,0.25);
}

.status-pill.warning {
  background: rgba(245,158,11,0.12);
  color: #92400e;
  border-color: rgba(245,158,11,0.25);
}

.status-pill.danger {
  background: rgba(239,68,68,0.12);
  color: #991b1b;
  border-color: rgba(239,68,68,0.25);
}

/* ============================
   ROW ACTION ICONS
============================ */

.row-actions {
  display: inline-flex;
  gap: 8px;
}

.icon-mini {
  height: 36px;
  width: 36px;
  border-radius: 12px;
  border: 1px solid var(--line);
  background: #fff;
  display: grid;
  place-items: center;
  cursor: pointer;
}

.icon-mini i {
  font-size: 18px;
}

.icon-mini:hover {
  transform: translateY(-1px);
}

.icon-mini.danger {
  background: rgba(239,68,68,0.1);
  border-color: rgba(239,68,68,0.25);
  color: #dc2626;
}

/* ============================
   EMPTY STATE
============================ */

.empty {
  display: flex;
  align-items: center;
  gap: 12px;
  color: #64748b;
}

.empty i {
  font-size: 26px;
}

/* ============================
   MODAL
============================ */

.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(2,6,23,0.55);
  display: grid;
  place-items: center;
  z-index: 100;
}

.modal {
  width: min(420px,92vw);
  background: #fff;
  border-radius: 22px;
  padding: 16px;
  border: 1px solid var(--line);
}

.modal-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.modal-head strong {
  font-weight: 900;
}

.modal-body {
  margin-top: 14px;
  display: grid;
  gap: 12px;
}

.modal-foot {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

/* ============================
   INPUTS
============================ */

.field label {
  font-size: 12px;
  font-weight: 800;
}

.input {
  height: 44px;
  width: 100%;
  border-radius: 14px;
  border: 1px solid var(--line);
  padding: 0 12px;
  font-size: 13px;
  font-weight: 600;
  outline: none;
}

.input:focus {
  border-color: rgba(46,109,231,0.4);
  box-shadow: 0 0 0 3px rgba(46,109,231,0.15);
}

.notes {
  width: 100%;
  min-height: 100px;
  border-radius: 14px;
  border: 1px solid var(--line);
  padding: 12px;
  resize: vertical;
  font-size: 13px;
}

/* ============================
   MOBILE
============================ */

@media (max-width: 900px) {
  .grid.cols-4 {
    grid-template-columns: repeat(2,1fr);
  }
}
/* ============================
   SEARCH + FILTER BAR
============================ */

.actions {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}

/* search box */

.search-mini {
  position: relative;
  min-width: 260px;
  height: 44px;
}

.search-mini i {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 18px;
  color: #94a3b8;
}

.search-mini input {
  width: 100%;
  height: 44px;
  border-radius: 16px;
  border: 1px solid var(--line);
  padding: 0 14px 0 42px;
  background: #fff;
  font-size: 13px;
  font-weight: 600;
  outline: none;
}

.search-mini input:focus {
  border-color: rgba(46,109,231,0.4);
  box-shadow: 0 0 0 4px rgba(46,109,231,0.12);
}

/* select dropdown */

.select {
  height: 44px;
  border-radius: 16px;
  border: 1px solid var(--line);
  padding: 0 14px;
  background: #fff;
  font-size: 13px;
  font-weight: 600;
  outline: none;
  min-width: 150px;
  cursor: pointer;
}

.select:focus {
  border-color: rgba(46,109,231,0.4);
  box-shadow: 0 0 0 4px rgba(46,109,231,0.12);
}

/* export button alignment */

.actions .btn {
  height: 44px;
  border-radius: 16px;
}

/* ============================
   MOBILE STACK
============================ */

@media (max-width: 980px) {
  .actions {
    flex-direction: column;
    align-items: stretch;
  }

  .search-mini {
    width: 100%;
    min-width: 0;
  }

  .select {
    width: 100%;
  }

  .actions .btn {
    width: 100%;
    justify-content: center;
  }
}

</style>